import type { LayoutCode } from './types';

// Default labels assuming the user is on a PC AZERTY-FR keyboard. Used as a
// fallback when the Keyboard API (navigator.keyboard.getLayoutMap) is not
// available. See MAC_AZERTY_LABELS for the Apple variant.
export const AZERTY_LABELS: Record<LayoutCode, string> = {
  Digit1: '&',
  Digit2: 'é',
  Digit3: '"',
  Digit4: "'",
  Digit5: '(',
  Digit6: '-',
  Digit7: 'è',
  Digit8: '_',
  Digit9: 'ç',
  Digit0: 'à',
  Minus: ')',
  Equal: '=',

  KeyQ: 'a',
  KeyW: 'z',
  KeyE: 'e',
  KeyR: 'r',
  KeyT: 't',
  KeyY: 'y',
  KeyU: 'u',
  KeyI: 'i',
  KeyO: 'o',
  KeyP: 'p',
  BracketLeft: '^',
  BracketRight: '$',

  KeyA: 'q',
  KeyS: 's',
  KeyD: 'd',
  KeyF: 'f',
  KeyG: 'g',
  KeyH: 'h',
  KeyJ: 'j',
  KeyK: 'k',
  KeyL: 'l',
  Semicolon: 'm',
  Quote: 'ù',
  Backslash: '*',

  KeyZ: 'w',
  KeyX: 'x',
  KeyC: 'c',
  KeyV: 'v',
  KeyB: 'b',
  KeyN: 'n',
  KeyM: ',',
  Comma: ';',
  Period: ':',
  Slash: '!',
};

// Apple's French layout differs from the PC AZERTY one on five of the cells
// used by the lattice: § instead of - on Digit6, ! instead of _ on Digit8,
// - instead of = on Equal, ` instead of * on Backslash and = instead of !
// on Slash. Everything else is identical, hence the spread.
export const MAC_AZERTY_LABELS: Record<LayoutCode, string> = {
  ...AZERTY_LABELS,
  Digit6: '§',
  Digit8: '!',
  Equal: '-',
  Backslash: '`',
  Slash: '=',
};

function isMacOS(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const platform = navigator.platform ?? '';

  if (platform.startsWith('Mac')) {
    // iPads report "MacIntel" too, but they have no physical lattice to label,
    // so treating them as macOS is harmless.
    return true;
  }

  return /Mac OS X/.test(navigator.userAgent ?? '');
}

// Static labels to show when the Keyboard API is unavailable (Safari and
// Firefox both refuse to implement it, for fingerprinting reasons).
export function getFallbackLayoutLabels(): Record<LayoutCode, string> {
  if (isMacOS()) {
    return MAC_AZERTY_LABELS;
  }

  return AZERTY_LABELS;
}

interface KeyboardLayoutMap {
  get(code: string): string | undefined;
}

interface NavigatorKeyboard {
  getLayoutMap(): Promise<KeyboardLayoutMap>;
}

interface NavigatorWithKeyboard extends Navigator {
  keyboard?: NavigatorKeyboard;
}

// Resolve display labels for each layout cell.
// Tries the Keyboard API first (Chromium only) so QWERTY/Dvorak users see
// their own labels; falls back to the AZERTY-FR defaults matching the host
// platform (Apple keyboards diverge from PC ones on a handful of keys).
export async function resolveLayoutLabels(
  codes: readonly LayoutCode[],
): Promise<Record<LayoutCode, string>> {
  const labels: Record<LayoutCode, string> = { ...getFallbackLayoutLabels() };

  if (typeof navigator === 'undefined') {
    return labels;
  }

  const nav = navigator as NavigatorWithKeyboard;

  if (!nav.keyboard?.getLayoutMap) {
    return labels;
  }

  try {
    const map = await nav.keyboard.getLayoutMap();

    for (const code of codes) {
      const value = map.get(code);

      if (value && value.length > 0) {
        labels[code] = value;
      }
    }
  } catch {
    // Keyboard API rejected, fall back silently.
  }

  return labels;
}
